<p-dialog
  header="Layout — Trade Study Inputs"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: 'min(60rem, 96vw)' }"
  [breakpoints]="{ '575px': '96vw' }"
  [(visible)]="visible"
  (onShow)="onDialogShow()"
>
  @let currencyCode = (currencyCode$ | async) ?? "GBP";
  <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 text-surface-switch-900">
    <div class="flex flex-col gap-6">
      <section class="flex flex-col gap-4">
        <h3 class="text-base font-semibold m-0 text-surface-switch-800">
          Production
        </h3>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="ts-total-assemblies">Total assemblies required</label>
            <p-inputNumber
              inputId="ts-total-assemblies"
              [(ngModel)]="total_assemblies_required"
              [min]="0"
              [showButtons]="true"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="ts-parts-per-asm">Parts per assembly</label>
            <p-inputNumber
              inputId="ts-parts-per-asm"
              [(ngModel)]="parts_per_assembly"
              [min]="0"
              [showButtons]="true"
              class="w-full"
            />
          </div>
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h3 class="text-base font-semibold m-0 text-surface-switch-800">
          Time
        </h3>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="ts-prog-years">Program duration (years)</label>
            <p-inputNumber
              inputId="ts-prog-years"
              [(ngModel)]="program_duration_years"
              [min]="0"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="ts-layout-shifts">Shifts</label>
            <p-inputNumber
              inputId="ts-layout-shifts"
              [(ngModel)]="layout_shifts"
              [min]="1"
              [showButtons]="true"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="ts-layout-hours">Hours per shift</label>
            <p-inputNumber
              inputId="ts-layout-hours"
              [(ngModel)]="layout_hours_per_shift"
              [min]="0"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="ts-layout-days">Days per year</label>
            <p-inputNumber
              inputId="ts-layout-days"
              [(ngModel)]="layout_days_per_year"
              [min]="0"
              class="w-full"
            />
          </div>
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h3 class="text-base font-semibold m-0 text-surface-switch-800">
          Cost
        </h3>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="ts-labor-rate"
              >Labor rate ({{ currencyCode }}/hr)</label
            >
            <p-inputNumber
              inputId="ts-labor-rate"
              [(ngModel)]="labor_rate_per_hour"
              mode="currency"
              [currency]="currencyCode"
              [minFractionDigits]="2"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="ts-overhead-pct">Overhead (%)</label>
            <p-inputNumber
              inputId="ts-overhead-pct"
              [(ngModel)]="overhead_percent"
              suffix="%"
              [minFractionDigits]="1"
              class="w-full"
            />
          </div>
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h3 class="text-base font-semibold m-0 text-surface-switch-800">
          Efficiency
        </h3>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <label for="ts-labor-util">Labor utilization</label>
            <p-inputNumber
              inputId="ts-labor-util"
              [(ngModel)]="labor_utilization"
              mode="decimal"
              [min]="0"
              [max]="1"
              [minFractionDigits]="1"
              [maxFractionDigits]="2"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="ts-layout-eff">Equipment efficiency</label>
            <p-inputNumber
              inputId="ts-layout-eff"
              [(ngModel)]="layout_equipment_efficiency"
              mode="decimal"
              [min]="0"
              [max]="1"
              [minFractionDigits]="2"
              [maxFractionDigits]="3"
              class="w-full"
            />
          </div>
        </div>
      </section>
    </div>

    <div
      class="flex flex-col gap-4 rounded-lg border border-surface-switch-300 bg-surface-switch-50 p-4 h-fit"
      aria-labelledby="ts-calculated-heading"
    >
      <div class="flex flex-col gap-1">
        <h3
          id="ts-calculated-heading"
          class="text-base font-semibold m-0 text-surface-switch-800"
        >
          Calculated
        </h3>
        <p class="m-0 text-xs text-surface-switch-500">
          Read-only — updates when inputs change.
        </p>
      </div>

      <div class="flex flex-col gap-4 mt-2">
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600">M-days per month</span>
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ mDaysPerMonth | number: "1.0-0" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Assemblies / month</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ assembliesPerMonth | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600">Parts / month</span>
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ partsPerMonth | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600">Assemblies / day</span>
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ assembliesPerDay | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600">Parts / day</span>
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ partsPerDay | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Cost / head / hr ({{ currencyCode }})</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{
              costPerHeadPerHour | currency: currencyCode : "symbol" : "1.4-4"
            }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Overhead rate ({{ currencyCode }} / hr)</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ overheadRate | currency: currencyCode : "symbol" : "1.4-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Hours Day 1 Shift + OT</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ hoursDay1ShiftPlusOt | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Hours Day 2 Shift + OT</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ hoursDay2ShiftPlusOt | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Hours Day 3 Shifts + OT</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ hoursDay3ShiftsPlusOt | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Usable Hours 1 Shift</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ usableHours1Shift | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Usable Hours 2 Shifts</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ usableHours2Shifts | number: "1.2-4" }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-surface-switch-600"
            >Usable Hours 3 Shifts</span
          >
          <span
            class="tabular-nums rounded-md border border-surface-switch-200 bg-surface-switch-0 px-3 py-2 text-base"
          >
            {{ usableHours3Shifts | number: "1.2-4" }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2 pt-2">
      <p-button label="Cancel" severity="secondary" (onClick)="close()" />
      <p-button
        label="Save"
        icon="pi pi-check"
        (onClick)="save()"
        [disabled]="loading || saving"
      />
    </div>
  </ng-template>
</p-dialog>
